import { useCallback, useRef, useState } from 'react'

import useSentry from 'Pods/Shared/Hooks/useSentry'

import { isDailyLimitError, toggleCommunityModelLike } from './communityModelLikeApi'

// Optimistic like toggle shared by every surface that likes a community model
// (the viewer footer and the gallery cards). The hook owns no like state: each
// caller passes `onLikeChange` and stores the result wherever its own state
// lives - local component state for the footer, the react-query cache for the
// gallery - so one implementation serves both without knowing about either.
//
// `onLikeChange` fires up to twice per toggle: once immediately with the
// optimistic values, then again with the server's authoritative response (or the
// previous values, on failure).
export default function useCommunityModelLike({ onLikeChange, dailyLimitMessage = '' }) {
  const { captureException } = useSentry()
  // Held in a ref rather than state: it guards re-entrancy for a single slug and
  // must not re-render every card in the gallery grid on each toggle.
  const inFlightSlugsRef = useRef(new Set())
  const [togglingCount, setTogglingCount] = useState(0)

  const toggleLike = useCallback(async ({ slug, liked, likeCount = 0 }) => {
    if (!slug || inFlightSlugsRef.current.has(slug)) return

    const nextLiked = !liked
    const nextCount = Math.max(0, likeCount + (nextLiked ? 1 : -1))

    inFlightSlugsRef.current.add(slug)
    setTogglingCount(count => count + 1)
    onLikeChange({ slug, liked: nextLiked, likeCount: nextCount })

    try {
      const payload = await toggleCommunityModelLike({ slug, liked: nextLiked })
      onLikeChange({
        slug,
        liked: typeof payload?.liked === 'boolean' ? payload.liked : nextLiked,
        likeCount: typeof payload?.likeCount === 'number' ? payload.likeCount : nextCount,
      })
    } catch (error) {
      onLikeChange({ slug, liked, likeCount })

      if (isDailyLimitError(error)) {
        if (dailyLimitMessage && typeof window !== 'undefined' && window.showNotification) {
          window.showNotification(dailyLimitMessage, { type: 'error', autohide: true })
        }
      } else {
        captureException(error)
      }
    } finally {
      inFlightSlugsRef.current.delete(slug)
      setTogglingCount(count => Math.max(0, count - 1))
    }
  }, [onLikeChange, dailyLimitMessage, captureException])

  return { toggleLike, isToggling: togglingCount > 0 }
}
